/**
 * CSSC Origins Module
 *
 * Contains origin types and priority definitions for the CSSC tracking system.
 * Full contract (who asserts what, manager mechanics, how to add an origin):
 * docs/features/cssc-origin-priority.md
 */
/**
 * Prioritized list of origin sources for attribution.
 * The order defines the precedence when determining the attribution origin, with sources listed earlier
 * taking precedence over those listed later.
 *
 * @remarks
 * - 'campaign_query_param': from `campaign` query parameter in the URL
 * - 'chosen_event_config': from CRM configuration for chosen event keyword,
 *   asserted by Flex/Streamlined Chosen experiences where the event overrides
 *   even an existing cookie (see "Event Overrides" in the CSSC Attribution doc)
 * - 'campaign_cookie': from existing `campaign` cookie
 * - 'http_referrer': from a referrer domain matching the list in referrers.ts
 * - 'how_did_you_hear': from the "How Did You Hear" selection in checkout app
 * - 'chosen_event_default': from CRM configuration for a chosen event keyword,
 *   asserted by the mainline Chosen Starter App as an experience-specific
 *   default only — an inbound campaign (query param or cookie) wins (DEV-7892)
 * - 'core4_page_config': from the `campaign_code` ACF field on Core4 pages
 * - 'donate_widget_config': from the `campaignId` value in a widget config
 * - 'global_default': default fallback for all sites
 *
 * @readonly
 */
export var originPriority = [
    'campaign_query_param',
    'chosen_event_config',
    'how_did_you_hear',
    'campaign_cookie',
    'http_referrer',
    'chosen_event_default',
    'core4_page_config',
    'donate_widget_config',
    'global_default'
];
/**
 * Returns the priority index of the given origin within the originPriority array.
 *
 * @param origin - The origin type to find the priority for
 * @returns The index of the origin in the originPriority array, or -1 if not found
 */
export var getPriorityIndex = function (origin) {
    return originPriority.indexOf(origin);
};
/**
 * Origins that are reserved for system use only and should not be manually asserted.
 * These origins are automatically set by the gatherCSSCSources function during initialization.
 *
 * @readonly
 */
export var RESERVED_ORIGINS = [
    'campaign_query_param',
    'campaign_cookie',
    'http_referrer',
    'global_default'
];
